import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';

import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';

import {
    DASHBOARD_V3_RETENTION_GRANULARITY_META,
    DASHBOARD_V3_RETENTION_GRANULARITY_TABS,
    DASHBOARD_V3_RETENTION_KPI_ACCENTS,
    DASHBOARD_V3_RETENTION_RATE_COLORS,
    DASHBOARD_V3_RETENTION_RATE_THRESHOLDS,
    DASHBOARD_V3_TRADING_PRODUCT_COLORS,
    DASHBOARD_V3_TRADING_PRODUCT_TABS,
} from '../../mock-data';
import { useDashboardV3RetentionData } from '../../hooks/useDashboardV3RetentionData';
import type {
    DashboardV3RetentionCohortRow,
    DashboardV3RetentionGranularity,
    DashboardV3RetentionKpiCard,
    DashboardV3RetentionProductType,
    DashboardV3RetentionTrendPoint,
} from '../../services/dashboardV3.types';

dayjs.extend(utc);
dayjs.extend(isoWeek);

const PANEL_SX = {
    bgcolor: '#232323',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 0.6,
    overflow: 'hidden',
} as const;

/**
 * 趋势图只固定高度和内边距，宽度实测容器后 1:1 画，
 * viewBox 与渲染像素同尺寸，hover 命中和浮层定位都不用再做缩放换算。
 */
const CHART_SVG_HEIGHT = 272;
const CHART_LEGEND_HEIGHT = 28;
const CHART_MIN_WIDTH = 640;
const CHART_LEFT = 56;
const CHART_RIGHT = 24;
const CHART_TOP = 24;
const CHART_BOTTOM = 32;

/** 四条刻度线按 0 / ⅓ / ⅔ / 1 摆放，跨度小于 3 时取整会撞出重复标签。 */
const MIN_Y_SPAN = 3;

/**
 * 队列矩阵列宽：前两列定宽，期数列按 table-layout: fixed 平分剩余空间，
 * 三个值同时用于算表格 minWidth，宽度和滚动阈值不会对不上。
 */
const MATRIX_LABEL_WIDTH = 150;
const MATRIX_COUNT_WIDTH = 100;
const MATRIX_PERIOD_WIDTH = 108;

/** 留存色块按正方形展示，边长要放得下 "100.0%" 这种最长的一行。 */
const MATRIX_CHIP_SIZE = 62;
const MATRIX_CHIP_SIZE_HEIGHT = 58;

/** 表头背景要盖掉主题里的 background.neutral（偏蓝）和 stickyHeader 的渐变。 */
const MATRIX_HEAD_SX = {
    color: 'rgba(255,255,255,0.62)',
    bgcolor: '#232323',
    backgroundImage: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.10)',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    py: 1.4,
} as const;

const MATRIX_CELL_SX = {
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    fontSize: 13,
    py: 1.1,
} as const;

/**
 * 首列 sticky 必须有不透明底色挡住横向滚过去的内容，
 * 行悬停色就得同时作用到所有单元格，否则整行会被切成深浅两段。
 */
const MATRIX_ROW_SX = {
    '& td': { bgcolor: '#232323', transition: 'background-color .15s ease' },
    '&:hover td': { bgcolor: '#2A2A2A' },
} as const;

const round2 = (value: number) => Math.round(value * 100) / 100;

type PlotPoint = { x: number; y: number };

/**
 * 单调三次样条：线条平缓，且保证两点之间不会冲出数据区间。
 * 普通 Catmull-Rom 在 0% 附近会甩出负值，看着像跌破 0。
 */
function buildSmoothSegment(pts: PlotPoint[]) {
    if (pts.length < 2) return '';

    const n = pts.length;
    const dx: number[] = [];
    const slopes: number[] = [];

    for (let i = 0; i < n - 1; i += 1) {
        dx[i] = pts[i + 1].x - pts[i].x;
        slopes[i] = (pts[i + 1].y - pts[i].y) / dx[i];
    }

    // 端点切线取相邻斜率；中间点在拐向相反时压成 0，这样曲线不会越过极值点
    const tangents: number[] = [slopes[0]];

    for (let i = 1; i < n - 1; i += 1) {
        if (slopes[i - 1] * slopes[i] <= 0) {
            tangents[i] = 0;
        } else {
            const w1 = 2 * dx[i] + dx[i - 1];
            const w2 = dx[i] + 2 * dx[i - 1];
            tangents[i] = (w1 + w2) / (w1 / slopes[i - 1] + w2 / slopes[i]);
        }
    }

    tangents[n - 1] = slopes[n - 2];

    let path = `M ${round2(pts[0].x)} ${round2(pts[0].y)}`;

    for (let i = 0; i < n - 1; i += 1) {
        const c1x = pts[i].x + dx[i] / 3;
        const c1y = pts[i].y + (tangents[i] * dx[i]) / 3;
        const c2x = pts[i + 1].x - dx[i] / 3;
        const c2y = pts[i + 1].y - (tangents[i + 1] * dx[i]) / 3;

        path += ` C ${round2(c1x)} ${round2(c1y)}, ${round2(c2x)} ${round2(c2y)}, ${round2(
            pts[i + 1].x
        )} ${round2(pts[i + 1].y)}`;
    }

    return path;
}

/** 实测容器宽度，图表跟着面板走，不写死画布尺寸。 */
function useContainerWidth<T extends HTMLElement>() {
    const ref = useRef<T | null>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const observer = new ResizeObserver(([entry]) => {
            setWidth(Math.round(entry?.contentRect.width ?? 0));
        });

        observer.observe(node);
        setWidth(Math.round(node.clientWidth));

        return () => observer.disconnect();
    }, []);

    return [ref, width] as const;
}

/** 留存率保留 1 位小数，拿不到（队列人数为 0 或该期未跑完）时显示「—」。 */
function formatRate(rate: number | null | undefined) {
    if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
    return `${rate.toFixed(1)}%`;
}

function formatCount(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return value.toLocaleString();
}

/** 阈值按粒度不同，与 PRD 一致。 */
function rateTone(rate: number | null, granularity: DashboardV3RetentionGranularity) {
    if (rate === null || !Number.isFinite(rate)) return null;

    const { good, medium } = DASHBOARD_V3_RETENTION_RATE_THRESHOLDS[granularity];
    if (rate >= good) return 'good' as const;
    if (rate >= medium) return 'medium' as const;
    return 'poor' as const;
}

function RetentionKpiCard({
    title,
    rate,
    delta,
    cohortUsers,
    retainedUsers,
    subtitle,
    accent,
    deltaNote,
    loading,
}: {
    title: string;
    rate: number | null;
    delta: number | null;
    cohortUsers: number;
    retainedUsers: number;
    subtitle: string;
    accent: string;
    deltaNote: string;
    loading: boolean;
}) {
    // 队列人数为 0 时留存率没有分母，上游按 0% 返回，这里统一显示「—」，
    // 免得和「有新用户但一个都没回来」混成同一个 0.0%；环比同样失去意义
    const hasCohort = Number.isFinite(cohortUsers) && cohortUsers > 0;
    const hasDelta = hasCohort && delta !== null && Number.isFinite(delta);
    const down = hasDelta && (delta as number) < 0;
    const deltaColor = !hasDelta ? 'rgba(255,255,255,0.45)' : down ? '#CF6679' : '#81C995';

    return (
        <Paper sx={{ ...PANEL_SX, position: 'relative', minHeight: 148, p: 2.4 }}>
            <Box sx={{ position: 'absolute', inset: '0 0 auto 0', height: 3, bgcolor: accent }} />

            <Typography
                sx={{
                    color: 'rgba(255,255,255,0.62)',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                }}
            >
                {title}
            </Typography>

            <Typography sx={{ color: '#FFFFFF', fontSize: 34, lineHeight: 1.15, mt: 1 }}>
                {loading ? <Skeleton width={120} height={44} /> : formatRate(hasCohort ? rate : null)}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1 }}>
                {loading ? (
                    <Skeleton width={150} height={20} />
                ) : (
                    <>
                        <Typography sx={{ color: deltaColor, fontSize: 13 }}>
                            {hasDelta
                                ? `${down ? '↓' : '↑'} ${Math.abs(delta as number).toFixed(1)}%`
                                : '—'}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                            {deltaNote}
                        </Typography>
                    </>
                )}
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 12, mt: 1 }}>
                {loading ? <Skeleton width="80%" height={18} /> : subtitle}
            </Typography>

            {!loading && (
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11.5, mt: 0.5 }}>
                    {`${formatCount(retainedUsers)} / ${formatCount(cohortUsers)}`}
                </Typography>
            )}
        </Paper>
    );
}

function RetentionTrendChart({
    points,
    labels,
    color,
    legend,
}: {
    points: DashboardV3RetentionTrendPoint[];
    labels: string[];
    color: string;
    legend: string;
}) {
    const [hovered, setHovered] = useState<number | null>(null);
    const [scrollRef, measuredWidth] = useContainerWidth<HTMLDivElement>();

    // 面板多宽画多宽；容器窄于下限时按下限画，交给外层横向滚动
    const svgWidth = Math.max(measuredWidth || CHART_MIN_WIDTH, CHART_MIN_WIDTH);
    const chartWidth = svgWidth - CHART_LEFT - CHART_RIGHT;
    const chartHeight = CHART_SVG_HEIGHT - CHART_TOP - CHART_BOTTOM;

    // 只有算得出留存率的点参与 Y 轴取值；该期还没跑完（null）的点位保留 x 轴位置，不画点
    const values = points
        .map((point) => point.retentionRate)
        .filter((rate): rate is number => rate !== null && Number.isFinite(rate));

    const rawMin = values.length ? Math.min(...values) : 0;
    const rawMax = values.length ? Math.max(...values) : 0;
    // 留存率波动通常很小，直接用 0~100 会压成一条直线，这里按数据范围留白
    const pad = Math.max((rawMax - rawMin) * 0.35, 2);
    const paddedMin = Math.max(0, Math.floor(rawMin - pad));
    const paddedMax = Math.min(100, Math.ceil(rawMax + pad));
    // 跨度太小（比如留存率全是 0，只留 0~2）时刻度取整会算出 0%/1%/1%/2% 这种重复标签
    const ySpan = Math.max(paddedMax - paddedMin, MIN_Y_SPAN);
    // 数据顶到 100% 时把下沿往回拉，保证最高一条刻度不会标出 101% 这种越界值
    const yMin = Math.max(0, Math.min(paddedMin, 100 - ySpan));

    const stepX = points.length > 1 ? chartWidth / (points.length - 1) : 0;

    const coords = points.map((point, index) => {
        const x = CHART_LEFT + (points.length > 1 ? index * stepX : chartWidth / 2);
        const rate = point.retentionRate;

        if (rate === null || !Number.isFinite(rate)) {
            return { x, y: null as number | null };
        }

        return { x, y: CHART_TOP + chartHeight - ((rate - yMin) / ySpan) * chartHeight };
    });

    // 按连续可画的点分段平滑，缺口处自然断开，不把两侧的点连成假趋势
    const linePath = coords
        .reduce<PlotPoint[][]>((runs, point) => {
            if (point.y === null) return [...runs, []];

            const rest = runs.slice(0, -1);
            const current = runs[runs.length - 1] ?? [];

            return [...rest, [...current, { x: point.x, y: point.y }]];
        }, [[]])
        .map(buildSmoothSegment)
        .filter(Boolean)
        .join(' ');

    const ticks = [0, 1 / 3, 2 / 3, 1];
    const hoveredPoint = hovered === null ? null : points[hovered];
    const hoveredCoord = hovered === null ? null : coords[hovered];
    const hoveredX = hoveredCoord?.x ?? 0;
    const hoveredY = hoveredCoord && hoveredCoord.y !== null ? hoveredCoord.y : null;
    // 该期没跑完时没有落点，浮层就贴着绘图区竖直居中，不至于跑到画布外面
    const tooltipY = hoveredY ?? CHART_TOP + chartHeight / 2;
    // 靠右侧时浮层翻到十字线左边，避免被面板裁掉
    const tooltipFlip = hoveredX > CHART_LEFT + chartWidth * 0.62;

    /** hover 整个画布即可，按横坐标就近吸附到某一期，不用非得压中那个小圆点 */
    const handleHoverMove = (event: React.MouseEvent<SVGRectElement>) => {
        if (!coords.length) return;

        const bounds = event.currentTarget.getBoundingClientRect();
        // viewBox 宽高和渲染像素一致，这里不需要再做缩放换算
        const offsetX = event.clientX - bounds.left;

        let nearest = 0;

        coords.forEach((point, index) => {
            if (Math.abs(point.x - offsetX) < Math.abs(coords[nearest].x - offsetX)) {
                nearest = index;
            }
        });

        setHovered(nearest);
    };

    return (
        <Box ref={scrollRef} sx={{ width: '100%', overflowX: 'auto' }}>
            <Box sx={{ width: svgWidth }}>
                {/* 定位容器与 viewBox 同尺寸，浮层可以直接用 SVG 坐标当像素值 */}
                <Box sx={{ position: 'relative', height: CHART_SVG_HEIGHT }}>
                    <Box
                        component="svg"
                        viewBox={`0 0 ${svgWidth} ${CHART_SVG_HEIGHT}`}
                        sx={{ width: svgWidth, height: CHART_SVG_HEIGHT, display: 'block' }}
                    >
                        {ticks.map((ratio) => {
                            const y = CHART_TOP + chartHeight - ratio * chartHeight;
                            return (
                                <g key={ratio}>
                                    <line
                                        x1={CHART_LEFT}
                                        x2={svgWidth - CHART_RIGHT}
                                        y1={y}
                                        y2={y}
                                        stroke="rgba(255,255,255,0.10)"
                                        strokeDasharray="4 4"
                                    />
                                    <text
                                        x={CHART_LEFT - 12}
                                        y={y + 4}
                                        textAnchor="end"
                                        fill="rgba(255,255,255,0.42)"
                                        fontSize="12"
                                    >
                                        {`${Math.round(yMin + ratio * ySpan)}%`}
                                    </text>
                                </g>
                            );
                        })}

                        {hovered !== null && (
                            <line
                                x1={coords[hovered].x}
                                x2={coords[hovered].x}
                                y1={CHART_TOP}
                                y2={CHART_TOP + chartHeight}
                                stroke="rgba(255,255,255,0.25)"
                            />
                        )}

                        <path
                            d={linePath}
                            fill="none"
                            stroke={color}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />

                        {coords.map((point, index) => {
                            const pointY = point.y;

                            return (
                                <g key={points[index].cohortFrom}>
                                    {pointY !== null && (
                                        <circle
                                            cx={point.x}
                                            cy={pointY}
                                            r={hovered === index ? 5 : 3.5}
                                            fill={hovered === index ? '#FFFFFF' : color}
                                            stroke={color}
                                            strokeWidth="2"
                                        />
                                    )}

                                    {/* 该期还没跑完时只留空位不画点，横轴日期照常显示 */}
                                    <text
                                        x={point.x}
                                        y={CHART_SVG_HEIGHT - 10}
                                        textAnchor="middle"
                                        fill="rgba(255,255,255,0.42)"
                                        fontSize="12"
                                    >
                                        {labels[index]}
                                    </text>
                                </g>
                            );
                        })}

                        {/* 命中层铺满画布并放在最后，鼠标进入图表区域就近吸附，不用去压那个小圆点 */}
                        <rect
                            x={0}
                            y={0}
                            width={svgWidth}
                            height={CHART_SVG_HEIGHT}
                            fill="transparent"
                            onMouseMove={handleHoverMove}
                            onMouseLeave={() => setHovered(null)}
                        />
                    </Box>

                    {hoveredPoint && hovered !== null && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: hoveredX,
                                top: tooltipY,
                                transform: tooltipFlip
                                    ? 'translate(calc(-100% - 14px), -50%)'
                                    : 'translate(14px, -50%)',
                                px: 1.4,
                                py: 1,
                                bgcolor: 'rgba(20,20,20,0.96)',
                                border: '1px solid rgba(255,255,255,0.14)',
                                borderRadius: 0.6,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap',
                                zIndex: 2,
                            }}
                        >
                            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                                {labels[hovered]}
                            </Typography>
                            <Typography sx={{ color, fontSize: 13, fontWeight: 600, mt: 0.4 }}>
                                {`${legend} : ${formatRate(hoveredPoint.retentionRate)}`}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11.5 }}>
                                {`${formatCount(hoveredPoint.retainedUsers)} / ${formatCount(
                                    hoveredPoint.cohortUsers
                                )}`}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 图例挪出 SVG 用 flex 居中：产品名长短不一，SVG 里量不到文字宽度只能硬编码偏移 */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.8}
                    sx={{ height: CHART_LEGEND_HEIGHT }}
                >
                    <Box component="svg" width={22} height={10} sx={{ flexShrink: 0 }}>
                        <line x1="0" y1="5" x2="22" y2="5" stroke={color} strokeWidth="2" />
                        <circle cx="11" cy="5" r="3.2" fill="#232323" stroke={color} strokeWidth="2" />
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 12 }}>
                        {legend}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
}

export function RetentionPage() {
    const t = useTranslate();

    const [productType, setProductType] = useState<DashboardV3RetentionProductType>('perps');
    const [granularity, setGranularity] = useState<DashboardV3RetentionGranularity>('day');

    const { cards, rows, trend, maxPeriod, kpiLoading, cohortsLoading } =
        useDashboardV3RetentionData({ productType, granularity });

    const meta = DASHBOARD_V3_RETENTION_GRANULARITY_META[granularity];
    const productColor = DASHBOARD_V3_TRADING_PRODUCT_COLORS[productType];
    const productLabel = useMemo(() => {
        const tab = DASHBOARD_V3_TRADING_PRODUCT_TABS.find((item) => item.value === productType);
        return tab ? t(tab.labelKey, { _: tab.label }) : productType;
    }, [productType, t]);

    // 上游给的都是 UTC 自然周期边界，一律按 UTC 显示，避免本地时区把日期挪一天
    const dayFormat = t('module.dashboardV3.retention.dateFormat.day', { _: 'MMM D' });
    const monthFormat = t('module.dashboardV3.retention.dateFormat.month', { _: 'MMM YYYY' });
    const trendDayFormat = t('module.dashboardV3.retention.dateFormat.trendDay', { _: 'M/D' });
    const trendMonthFormat = t('module.dashboardV3.retention.dateFormat.trendMonth', {
        _: 'YYYY/MM',
    });

    const cohortLabel = (cohortFrom: string, cohortTo: string) => {
        const from = dayjs.utc(cohortFrom);
        if (granularity === 'month') return from.format(monthFormat);
        if (granularity === 'day') return from.format(dayFormat);
        // 自然周展示周一至周日，cohortTo 是右开区间要减一天
        return `${from.format(dayFormat)} – ${dayjs.utc(cohortTo).subtract(1, 'day').format(dayFormat)}`;
    };

    const trendLabel = (cohortFrom: string) => {
        const from = dayjs.utc(cohortFrom);
        if (granularity === 'month') return from.format(trendMonthFormat);
        if (granularity === 'day') return from.format(trendDayFormat);
        return `W${String(from.isoWeek()).padStart(2, '0')}`;
    };

    const periodColumns = useMemo(
        () => Array.from({ length: maxPeriod || meta.maxPeriod }, (_, index) => index + 1),
        [maxPeriod, meta.maxPeriod]
    );

    // 期数随粒度变（日 7 / 周 8 / 月 12），表格下限跟着列数算，不写死一个值
    const matrixMinWidth =
        MATRIX_LABEL_WIDTH + MATRIX_COUNT_WIDTH + periodColumns.length * MATRIX_PERIOD_WIDTH;

    const trendLabels = useMemo(() => trend.map((point) => trendLabel(point.cohortFrom)), [trend, granularity, trendMonthFormat, trendDayFormat]); // eslint-disable-line react-hooks/exhaustive-deps

    const kpiSubtitleKeys: Record<string, { label: string; subtitle: string; deltaNote: string }> = {
        daily: {
            label: t('module.dashboardV3.retention.kpi.daily', { _: 'D+1 Retention' }),
            subtitle: t('module.dashboardV3.retention.kpi.dailyDesc', {
                product: productLabel,
                _: `Yesterday's new ${productLabel} users trading again today`,
            }),
            deltaNote: t('module.dashboardV3.retention.kpi.vsPrior', { _: 'vs Prior' }),
        },
        weekly: {
            label: t('module.dashboardV3.retention.kpi.weekly', { _: 'Weekly Retention' }),
            subtitle: t('module.dashboardV3.retention.kpi.weeklyDesc', {
                product: productLabel,
                _: `Last week's new ${productLabel} users trading again this week`,
            }),
            deltaNote: t('module.dashboardV3.retention.kpi.vsLastWeek', { _: 'vs Last Week' }),
        },
        monthly: {
            label: t('module.dashboardV3.retention.kpi.monthly', { _: 'Monthly Retention' }),
            subtitle: t('module.dashboardV3.retention.kpi.monthlyDesc', {
                product: productLabel,
                _: `Last month's new ${productLabel} users trading again this month`,
            }),
            deltaNote: t('module.dashboardV3.retention.kpi.vsLastMonth', { _: 'vs Last Month' }),
        },
    };

    const orderedCards: DashboardV3RetentionKpiCard[] = useMemo(() => {
        const order = ['daily', 'weekly', 'monthly'];
        return [...cards].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }, [cards]);

    const cohortPanelTitle = {
        day: t('module.dashboardV3.retention.matrix.daily', { _: 'Daily Cohort Retention' }),
        week: t('module.dashboardV3.retention.matrix.weekly', { _: 'Weekly Cohort Retention' }),
        month: t('module.dashboardV3.retention.matrix.monthly', { _: 'Monthly Cohort Retention' }),
    }[granularity];

    const trendRangeNote = {
        day: t('module.dashboardV3.retention.trend.lastDays', { _: 'Last 14 days' }),
        week: t('module.dashboardV3.retention.trend.lastWeeks', { _: 'Last 12 weeks' }),
        month: t('module.dashboardV3.retention.trend.lastMonths', { _: 'Last 12 months' }),
    }[granularity];

    return (
        <PermissionGuard resource="dashboard-v3" action="read" fallback={<ErrorPage403 />}>
            <Box sx={{ mt: 3, color: '#FFFFFF' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.6 }}>
                            {t('module.dashboardV3.retention.title', { _: 'Retention Analysis' })}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap">
                            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                                {t('module.dashboardV3.retention.subtitle', {
                                    _: 'New trader retention · Non-rolling · UTC+0',
                                })}
                            </Typography>
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: 4,
                                    bgcolor: 'rgba(3,218,198,0.14)',
                                    color: '#03DAC6',
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                {t('module.dashboardV3.retention.calendarBadge', {
                                    _: 'Weekly/monthly by calendar period',
                                })}
                            </Box>
                        </Stack>
                    </Box>

                    {/* 粒度切换只影响下面的趋势图和队列矩阵，KPI 卡片固定不变 */}
                    <Stack direction="row" spacing={1}>
                        {DASHBOARD_V3_RETENTION_GRANULARITY_TABS.map((item) => {
                            const active = granularity === item.value;

                            return (
                                <Box
                                    key={item.value}
                                    onClick={() => setGranularity(item.value)}
                                    sx={{
                                        px: 2.2,
                                        py: 0.7,
                                        borderRadius: 4,
                                        fontSize: 13,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        color: active ? '#BB86FC' : 'rgba(255,255,255,0.62)',
                                        border: `1px solid ${active ? '#BB86FC' : 'rgba(255,255,255,0.18)'}`,
                                        bgcolor: active ? 'rgba(187,134,252,0.10)' : 'transparent',
                                        transition: 'all .2s',
                                        '&:hover': { borderColor: '#BB86FC' },
                                    }}
                                >
                                    {t(item.labelKey, { _: item.label })}
                                </Box>
                            );
                        })}
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        mb: 2.5,
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        flexWrap: 'wrap',
                    }}
                >
                    {DASHBOARD_V3_TRADING_PRODUCT_TABS.map((tab) => {
                        const active = productType === tab.value;
                        const activeColor = DASHBOARD_V3_TRADING_PRODUCT_COLORS[tab.value];

                        return (
                            <Box
                                key={tab.value}
                                onClick={() => setProductType(tab.value)}
                                sx={{
                                    py: 1.2,
                                    px: 0.5,
                                    cursor: 'pointer',
                                    color: active ? activeColor : 'rgba(255,255,255,0.56)',
                                    fontSize: 14,
                                    fontWeight: active ? 700 : 600,
                                    borderBottom: active
                                        ? `2px solid ${activeColor}`
                                        : '2px solid transparent',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {t(tab.labelKey, { _: tab.label })}
                            </Box>
                        );
                    })}
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2.5,
                    }}
                >
                    {(['daily', 'weekly', 'monthly'] as const).map((key) => {
                        const card = orderedCards.find((item) => item.key === key);
                        const copy = kpiSubtitleKeys[key];

                        return (
                            <RetentionKpiCard
                                key={key}
                                title={copy.label}
                                rate={card?.retentionRate ?? null}
                                delta={card?.retentionRateDelta ?? null}
                                cohortUsers={card?.cohortUsers ?? 0}
                                retainedUsers={card?.retainedUsers ?? 0}
                                subtitle={copy.subtitle}
                                deltaNote={copy.deltaNote}
                                accent={DASHBOARD_V3_RETENTION_KPI_ACCENTS[key]}
                                loading={kpiLoading}
                            />
                        );
                    })}
                </Box>

                <Paper sx={{ ...PANEL_SX, mb: 2.5 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2.6, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                    >
                        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                            {`${meta.periodPrefix}+1 ${t('module.dashboardV3.retention.trend.title', {
                                _: 'Retention Trend',
                            })}`}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 13 }}>
                            {trendRangeNote}
                        </Typography>
                    </Stack>

                    <Box sx={{ px: 2.6, py: 2.4 }}>
                        {cohortsLoading ? (
                            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
                        ) : trend.length ? (
                            <RetentionTrendChart
                                points={trend}
                                labels={trendLabels}
                                color={productColor}
                                legend={`${productLabel} ${meta.periodPrefix}+1`}
                            />
                        ) : (
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.42)',
                                    fontSize: 13,
                                    textAlign: 'center',
                                    py: 10,
                                }}
                            >
                                {t('module.dashboardV3.retention.trend.empty', {
                                    _: 'No completed retention periods yet',
                                })}
                            </Typography>
                        )}
                    </Box>
                </Paper>

                <Paper sx={PANEL_SX}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2.6, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                    >
                        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                            {cohortPanelTitle}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 13 }}>
                            {t('module.dashboardV3.retention.matrix.trackedUpTo', {
                                _: 'Tracked up to',
                            })}
                            {` ${meta.periodPrefix}+${maxPeriod || meta.maxPeriod}`}
                        </Typography>
                    </Stack>

                    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                        {/*
                          * 定宽布局：前两列给固定宽度，期数列平分剩余空间，列距才不会被内容长度带偏
                          * （auto 布局下浏览器会把富余宽度几乎全塞给首列）。
                          * 列宽合计当 minWidth，窄于它就交给容器横向滚动，不写死表宽。
                          */}
                        <Table
                            size="small"
                            sx={{ width: '100%', minWidth: matrixMinWidth, tableLayout: 'fixed' }}
                        >
                            <colgroup>
                                <col style={{ width: MATRIX_LABEL_WIDTH }} />
                                <col style={{ width: MATRIX_COUNT_WIDTH }} />
                                {periodColumns.map((periodNumber) => (
                                    <col key={periodNumber} />
                                ))}
                            </colgroup>

                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...MATRIX_HEAD_SX,
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        }}
                                    >
                                        {granularity === 'week'
                                            ? t('module.dashboardV3.retention.matrix.week', { _: 'Week' })
                                            : granularity === 'month'
                                              ? t('module.dashboardV3.retention.matrix.month', { _: 'Month' })
                                              : t('module.dashboardV3.retention.matrix.date', { _: 'Date' })}
                                    </TableCell>
                                    <TableCell align="left" sx={MATRIX_HEAD_SX}>
                                        {t('module.dashboardV3.retention.matrix.newUsers', {
                                            _: 'New Users',
                                        })}
                                    </TableCell>
                                    {periodColumns.map((periodNumber) => (
                                        <TableCell
                                            key={periodNumber}
                                            align="center"
                                            sx={MATRIX_HEAD_SX}
                                        >
                                            {`${meta.periodPrefix}+${periodNumber}`}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {cohortsLoading ? (
                                    Array.from({ length: 6 }).map((_unused, index) => (
                                        <TableRow key={index}>
                                            <TableCell
                                                colSpan={periodColumns.length + 2}
                                                sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                                            >
                                                <Skeleton height={28} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : rows.length ? (
                                    rows.map((row: DashboardV3RetentionCohortRow) => (
                                        <TableRow key={row.cohortFrom} sx={MATRIX_ROW_SX}>
                                            <TableCell
                                                sx={{
                                                    ...MATRIX_CELL_SX,
                                                    color: '#FFFFFF',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                }}
                                            >
                                                {cohortLabel(row.cohortFrom, row.cohortTo)}
                                            </TableCell>

                                            <TableCell
                                                align="left"
                                                sx={{
                                                    ...MATRIX_CELL_SX,
                                                    color: 'rgba(255,255,255,0.86)',
                                                }}
                                            >
                                                {formatCount(row.cohortUsers)}
                                            </TableCell>

                                            {periodColumns.map((periodNumber) => {
                                                const cell = row.periods?.find(
                                                    (item) => item.periodNumber === periodNumber
                                                );
                                                const tone = rateTone(
                                                    cell?.retentionRate ?? null,
                                                    granularity
                                                );

                                                return (
                                                    <TableCell
                                                        key={periodNumber}
                                                        align="center"
                                                        sx={{ ...MATRIX_CELL_SX, px: 1.25 }}
                                                    >
                                                        {tone ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    // 定宽定高保证是正方形，不随列宽被拉长
                                                                    mx: 'auto',
                                                                    width: MATRIX_CHIP_SIZE,
                                                                    height: MATRIX_CHIP_SIZE_HEIGHT,
                                                                    borderRadius: 0.75,
                                                                    bgcolor:
                                                                        DASHBOARD_V3_RETENTION_RATE_COLORS[
                                                                            tone
                                                                        ].bg,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        color: DASHBOARD_V3_RETENTION_RATE_COLORS[
                                                                            tone
                                                                        ].text,
                                                                        fontSize: 13,
                                                                        fontWeight: 700,
                                                                        lineHeight: 1.3,
                                                                    }}
                                                                >
                                                                    {formatRate(cell?.retentionRate)}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        color: 'rgba(255,255,255,0.45)',
                                                                        fontSize: 11,
                                                                        lineHeight: 1.3,
                                                                        marginTop: 0.5,
                                                                    }}
                                                                >
                                                                    {formatCount(cell?.retainedUsers)}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography
                                                                sx={{
                                                                    color: 'rgba(255,255,255,0.28)',
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={periodColumns.length + 2}
                                            sx={{
                                                py: 6,
                                                textAlign: 'center',
                                                color: 'rgba(255,255,255,0.42)',
                                                fontSize: 13,
                                                borderBottom: 'none',
                                            }}
                                        >
                                            {t('module.dashboardV3.retention.matrix.empty', {
                                                _: 'No cohort data',
                                            })}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </PermissionGuard>
    );
}
